import React, { useEffect, useRef, useState, useMemo } from "react";
import { DashboardLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import {
  treatmentPlanQuestions,
  getCareplanValues,
  getPromise,
  getPromiseToast,
  groupQuestionsByCategory,
  groupQuestionsByCategoryTreatmentPlan,
  formatDate,
  getErrToast,
} from "@/utils/helpers";
import RHFWrapper from "@/components/RHFWrapper";
import RhfInput from "@/components/rhfFields/RhfInput";
import RhfDateTimePicker from "@/components/rhfFields/Rhfdatetimepicker";
import RhfMultiInput from "@/components/rhfFields/RhfMultiInput";
import RhfSelect from "@/components/rhfFields/RhfSelect";
import RhfChecked from "@/components/rhfFields/RhfChecked";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useGetData } from "@/hooks/useGetData";
import { useIsMobile } from "@/hooks/use-mobile"

import { X, CheckCircle2, Download, IdCard, Heart, Droplet, Wind, Scale } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AxiosInstance from "@/utils/AxiosInstance";
import { downloadBackendPdf } from "@/utils/downloadBackendPdf";
import { FormLabel } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/use-auth";
import { toast } from "sonner";
import { useWatch } from "react-hook-form";
import ViewTreatmentPlan from "./ViewTreatmentPlan";

type Props = {
  isEdit?: boolean;
  isView?: boolean;
};

// Device Type Configuration
const tabConfig = [
  { name: "Blood Pressure", value: "blood_pressure", deviceType: "blood_pressure", icon: Heart },
  { name: "Blood Glucose", value: "blood_glucose", deviceType: "glucose_meter", icon: Droplet },
  { name: "Blood Oxygen", value: "pulse_oximeter", deviceType: "pulse_oximeter", icon: Wind },
  { name: "Weight Scale", value: "weight_scale", deviceType: "weight_scale", icon: Scale },
];

// Device Type Selection Popup
const DeviceTypePopup = ({
  isOpen,
  onCancel,
  onDone,
  isEdit,
  btnLoad,
  patientId,
  typeOfPatient,
  seletedPreviousDeviceTypes,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onDone: (selected: string[]) => void;
  isEdit: boolean;
  btnLoad: boolean;
  patientId?: string;
  typeOfPatient?: string;
  seletedPreviousDeviceTypes?: string[];
}) => {
  const [tempSelected, setTempSelected] = useState<string[]>([]);
  // console.log("🔥 tempSelected", tempSelected);
  // For outpatient: fetch only assigned devices; for facility: show all
  const IS_OUTPATIENT = typeOfPatient === "outpatient";
  const { apiData: devicesData, loading: devicesLoading } = useGetData(
    "/devices",
    { patientId },
    false,
    !!isOpen && !!patientId && IS_OUTPATIENT,
    () => { }
  );
  const assignedDevices: any[] = devicesData?.data?.devices || [];
  const assignedDeviceTypes = Array.from(
    new Set(assignedDevices.map((d: any) => d?.deviceType).filter(Boolean))
  ) as string[];

  const devicesToShow = IS_OUTPATIENT
    ? tabConfig.filter((d) => assignedDeviceTypes.includes(d.deviceType))
    : tabConfig;

    // console.log("🔥 DeviceTypePopup", {
    //   isOpen,
    //   tempSelected,
    // });

useEffect(() => {
  if (isOpen && seletedPreviousDeviceTypes && seletedPreviousDeviceTypes.length > 0) {
    setTempSelected([]);
  }
}, [seletedPreviousDeviceTypes, isOpen]);

  if (!isOpen) return null;

  const handleCheckboxChange = (deviceType: string) => {
    setTempSelected((prev) => {
      if (prev.includes(deviceType)) {
        return prev.filter((d) => d !== deviceType);
      } else {
        return [...prev, deviceType];
      }
    });
  };

  const handleDone = () => {
    if (tempSelected.length > 0) {
      onDone(tempSelected);
    }
  };

  const previousDevices = (seletedPreviousDeviceTypes ?? [])
  .map((type) => tabConfig.find((d) => d.deviceType === type))
  .filter(Boolean);

  const isDoneDisabled = tempSelected.length === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in-0"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative z-50 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 animate-in fade-in-0 zoom-in-95 duration-200">
        {/* Header */}
        <div className="relative p-6 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Select Device Types
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Choose at least one device type to continue
              </p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {IS_OUTPATIENT && devicesLoading ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Loading assigned devices...
            </p>
          ) : IS_OUTPATIENT && devicesToShow.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No devices assigned to this patient. Please assign devices first.
            </p>
          ) : (
            <div className="space-y-3">
              {devicesToShow.map((device) => {
                const IconComponent = device.icon;
                const isSelected = tempSelected.includes(device.deviceType);
                return (
                  <label
                    key={device.value}
                    className={`flex items-center p-4 rounded-xl border-2 transition-all duration-200 ${isSelected
                      ? "border-blue-500 bg-indigo-50 shadow-md cursor-pointer"
                      : "border-gray-200 hover:border-indigo-300 hover:bg-gray-50 cursor-pointer"
                      }`}
                  >
                    <div className="flex items-center flex-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleCheckboxChange(device.deviceType)}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                      />
                      <div className="flex items-center ml-3">
                        {IconComponent && <IconComponent className="w-5 h-5 mr-2 text-gray-700" />}
                        <span
                          className={`font-medium ${isSelected ? "text-indigo-900" : "text-gray-700"
                            }`}
                        >
                          {device.name}
                        </span>
                      </div>
                    </div>
                    {isSelected && (
                      <CheckCircle2 className="text-blue-600" size={20} />
                    )}
                  </label>
                );
              })}
            </div>
          )}

          {/* Selection Counter */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-center text-blue-700">
              <span className="font-semibold">{tempSelected.length}</span> device type{tempSelected.length !== 1 ? 's' : ''} selected
              {tempSelected.length === 0 && (
                <span className="block text-xs mt-1 text-red-600 font-medium">
                  Please select at least one device type
                </span>
              )}
            </p>
          </div>
        </div>

        {isEdit && previousDevices.length > 0 && (
  <div className="px-6 pb-4 border-t border-gray-100 pt-4">
    <p className="text-sm font-medium text-gray-700 mb-2">
      Previously selected devices
    </p>
    <div className="flex flex-wrap gap-2">
      {previousDevices.map((device) => {
        const Icon = device?.icon;
        return (
          <span
            key={device?.deviceType}
            className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700"
          >
            {Icon && <Icon className="h-4 w-4" />}
            {device?.name}
          </span>
        );
      })}
    </div>
  </div>
)}

        {/* Footer */}
        <div className="p-6 pt-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <Button variant={"outline"} className="w-fit" onClick={onCancel}>
            Cancel
          </Button>

          <Button
            onClick={handleDone}
            disabled={isDoneDisabled || btnLoad}
            className="w-fit"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};




// Updated Careplan Component
const Careplan = ({ isEdit = false, isView: isViewProp = false }: Props) => {

  const isMobile = useIsMobile();
  // Removed isPopupOpen - chronic disease popup is not used
  const [fetchingQuestion, setFetchingQuestion] = useState(false);
  const [searchParam] = useSearchParams();
  const IS_FOLLOW_UP = searchParam.get("type") === "followup";
  const IS_PCM = searchParam.get('plan') === 'pcm';
  const isView = isViewProp || searchParam.get("view") === "true";
  const navigate = useNavigate();
  const { patientId, careplanId, id } = useParams();
  const [careplanData, setCareplanData] = useState<any>(null);

  const { user } = useAuth();
  const IS_ROLE_CARETAKER = user?.role == "caretaker";

  const [patientData, setPatientData] = useState<any>({});

  // Caretaker: use self id (user.id / user.caretakerId); others use careplanData
  const [selectedCaretaker, setSelectedCaretaker] = useState(
    IS_ROLE_CARETAKER
      ? (user?.id ?? user?.caretakerId ? String(user?.id ?? user?.caretakerId) : undefined)
      : null,
  );

  // Check if caretaker is in the assigned caretakers list
  const isCaretakerAssigned = useMemo(() => {
    if (!IS_ROLE_CARETAKER) return true; // Not a caretaker, so always allow
    if (!Array.isArray(patientData?.assignedCaretakerIds)) return false;
    return patientData.assignedCaretakerIds.some(
      (caretaker: any) => caretaker?.email?.toLowerCase() === user?.email?.toLowerCase() || caretaker._id === user?.id || caretaker._id === user?.caretakerId
    );
  }, [IS_ROLE_CARETAKER, patientData?.assignedCaretakerIds, user?.email, user?.id, user?.caretakerId]);
  console.log("🔥 isCaretakerAssigned", isCaretakerAssigned);
  const [chronicQuestions, setChronicQuestions] = useState([]);
  const [btnLoad, setbtnLoad] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const careplanContentRef = useRef<HTMLDivElement>(null);
  const [caretakerOptions, setCaretakerOptions] = useState<any[]>([]);
  const { loading: patientLoading, err: patientErr } = useGetData(
    "/patients/" + patientId,
    {},
    false,
    !!patientId,
    (err, data) => {
      if (!err && data?.data) {
        setPatientData(data.data);
        setCaretakerOptions(data.data?.assignedCaretakerIds || []);
        // Add mode + caretaker: set selected caretaker to current user from assigned list
        if (!careplanId && IS_ROLE_CARETAKER && (data.data?.assignedCaretakerIds?.length ?? 0) > 0) {
          const caretakerId = (Array.isArray(data.data.assignedCaretakerIds)
            ? data.data.assignedCaretakerIds.find((e: any) => e?.email === user?.email)?._id
            : undefined) ?? (data.data.assignedCaretakerIds?.[0]?._id ?? data.data.assignedCaretakerIds?.[0]);
          if (caretakerId) setSelectedCaretaker(String(caretakerId));
        }
      }
    },
  );
  const AllChronicDiseases = [
    patientData?.dx1,
    patientData?.dx2,
    patientData?.dx3,
    patientData?.dx4,
    patientData?.dx5,
  ].filter((v) => v);

  const [selectedChronic, setSelectedChronic] = useState(
    IS_PCM ? ([careplanData?.icdCode || ""]) :
      (careplanData?.icdCodes || []),
  );
  const [selectedDeviceTypes, setSelectedDeviceTypes] = useState<string[]>(
    careplanData?.deviceType ? (Array.isArray(careplanData.deviceType) ? careplanData.deviceType : [careplanData.deviceType]) : []
  );

  const icdCodesTopDisplay = useMemo(() => {
    const arr = (selectedChronic || [])
      .filter((c: any) => c != null && String(c).trim() !== "")
      .map((c: any) => String(c).trim());
    return arr.length > 0 ? arr.join(", ") : "";
  }, [selectedChronic]);

  const [isDeviceTypePopupOpen, setIsDeviceTypePopupOpen] = useState(false);
  console.log({ selectedChronic, selectedDeviceTypes });
  const allCategories = groupQuestionsByCategoryTreatmentPlan(
    [...treatmentPlanQuestions,
    ...(selectedDeviceTypes.length > 0 ? selectedDeviceTypes.map((deviceType: any, i) => ({
      question:
        `Your provider has selected the following frequency recommendations for the ${deviceType} : (select all that apply)`,
      // questionrandomid: "weight_scale_001" + i + 1,
      questionrandomid: `device_frequency_${deviceType}`,
      options: [
        "Once per day (daily)",
        "Twice per day",
        "Three times per day",
        "Every other day",
        "Right before bed",
        "Before Breakfast",
        "After Breakfast",
        "Before Lunch",
        "After Lunch",
        "Before Dinner",
        "After Dinner",
        "Before taking any medication",
        "2-3 hours after taking any medication",
      ],
      category: "ENROLLMENT",
      type: "multiple_choice",
    })) : []),
    ],
    chronicQuestions,
  );
  console.log({ allCategories });

  const { loading: treatmentPlanLoading } = useGetData('/treatment-plan/' + careplanId, {}, false, !!careplanId, (err, data) => {
    if (!err && data?.data) {
      setCareplanData(data.data);
      setSelectedChronic(data.data?.icdCodes || []);
      if (data.data?.deviceType) {
        setSelectedDeviceTypes(Array.isArray(data.data.deviceType) ? data.data.deviceType : [data.data.deviceType]);
      }
      if (data.data?.careTakerId?._id) {
        setSelectedCaretaker(data.data?.careTakerId?._id);
      }
      if (data.data?.patientId && typeof data.data.patientId === "object") {
        setPatientData(data.data.patientId);
      }
    }
  });

  const isViewLoading =  (!!patientLoading || !!treatmentPlanLoading || (!!careplanId && !careplanData));
  // console.log("🔥 selectedCaretaker", selectedCaretaker , caretakerOptions);

  // Treatment Plan page does not use icd-questionnaire/by-code API; use empty chronic questions
  const getQuestionBySelectedICDCodes = async (selected: string[], cb: any) => {
    setFetchingQuestion(true);
    try {
      setChronicQuestions([]);
      cb();
    } finally {
      setFetchingQuestion(false);
    }
  };

  // Removed handlePopupCancel and handlePopupDone - chronic disease popup is not used

  const handleDeviceTypePopupCancel = () => {
    setIsDeviceTypePopupOpen(false);
    navigate(-1);
  };

  const handleDeviceTypePopupDone = (selected: string[]) => {
    console.log("🔥 POPUP DONE", selected);

    setSelectedDeviceTypes(selected);
    console.log("🔥 CLOSING POPUP");


    setIsDeviceTypePopupOpen(false);
    // After device type selection, load questions using patient's chronic diseases
    // Same as Care Plan - call icd-questionnaire/by-code API
    if (AllChronicDiseases.length > 0) {
      // Use patient's chronic diseases to fetch questions
      // Determine maxSelect based on patient programs (same logic as Care Plan)
      const maxSelect = Array.isArray(patientData?.programs)
        ? patientData.programs.includes("ccm")
          ? 2
          : patientData.programs.includes("pcm")
            ? 1
            : 0
        : 0;

      const chronicDiseasesToUse = maxSelect > 0
        ? AllChronicDiseases.slice(0, maxSelect)
        : AllChronicDiseases;

      if (chronicDiseasesToUse.length > 0) {
        setSelectedChronic(chronicDiseasesToUse);
        getQuestionBySelectedICDCodes(chronicDiseasesToUse, () => {
          // Questions fetched, continue with form
        });
      }
    }
  };
  async function handleSubmitCareplan(formdata: any) {
    let finalData = allCategories
      .map((cat: any) => cat.questions)
      .flat()
      .map((ques: any) => ({
        ...ques,
        answer: formdata?.[ques.questionrandomid]
          ? formdata?.[ques.questionrandomid]
          : ques?.type == "multiple_choice"
            ? []
            : "",
      }));
    console.log({ allCategories, formdata, finalData });
    
    // return;
    const { pr, resolveFn, rejectFn } = getPromise();
    getPromiseToast(pr);
    try {
      setbtnLoad(true);

      const isValidCaretaker =
  selectedCaretaker &&
  caretakerOptions.some((c) => c._id === selectedCaretaker);
if (!isValidCaretaker && !IS_ROLE_CARETAKER) {
  throw new Error("Please select a caretaker assigned to this patient");
}
      if (!selectedCaretaker && !IS_ROLE_CARETAKER) {
        throw new Error("Caretaker is mandatory");
      }
      if (!selectedDeviceTypes || selectedDeviceTypes.length === 0) {
        throw new Error("Please select at least one device type");
      }

      // Extract conditions from selected chronic diseases (ICD codes)
      const conditions = selectedChronic || [];

      let payload = {
        patientId: patientData?._id,
        careTakerId: selectedCaretaker,
        icdCodes: selectedChronic,
        icdCode: selectedChronic?.[0],
        questions: finalData,
        status: "submitted",
        conditions: conditions,
        deviceType: selectedDeviceTypes,
      };
      // console.log({payload})
      // return;
      if (isEdit && !IS_FOLLOW_UP) {
        const { data } = await AxiosInstance().put(
          "/treatment-plan/" + careplanData?._id,
          payload,
        );

        resolveFn("Treatment plan updated successfully");
        const profilePatientId = patientData?._id || patientId;
        if (profilePatientId) {
          navigate(
            `/patients/${profilePatientId}?tab=nursing-notes&openNursingNote=1`,
            { replace: true },
          );
        } else {
          navigate(-1);
        }
      } else {
        await AxiosInstance().post("/treatment-plan", payload);

        resolveFn("Treatment plan added successfully");
        const profilePatientId = patientData?._id || patientId;
        if (profilePatientId) {
          navigate(
            `/patients/${profilePatientId}?tab=nursing-notes&openNursingNote=1`,
            { replace: true },
          );
        } else {
          navigate(-1);
        }
      }
    } catch (error) {
      console.log(error);
      rejectFn(error);
    } finally {
      setbtnLoad(false);
    }
  }
  console.log({patientData})
  useEffect(() => {
    if (patientData && Object.keys(patientData).length > 0 && !isView) {
      // Show device type popup directly instead of chronic disease popup
      if (selectedDeviceTypes.length === 0 || isEdit) {
        setIsDeviceTypePopupOpen(true);
      }
    }
  }, [patientData, isView, isEdit]);

  // Device type popup is shown directly via the useEffect above (line 542-549)
  // console.log("----", {
  //   patientData,
  //   careplanData,
  //   selectedChronic,
  //   allCategories,
  //   chronicQuestions
  // });

  useEffect(() => {
    // console.log({ selectedChronic, chronicQuestions }, "EEFECT");
    if (selectedChronic && selectedChronic?.length > 0 && (isEdit || isView)) {
      if (isEdit || isView) {
        setChronicQuestions(
          (careplanData?.questions && Array.isArray(careplanData?.questions) ? careplanData?.questions : []).filter(
            (ques: any) =>
              ques?.questionrandomid &&
              ques?.questionrandomid?.includes("chronic"),
          ),
        );
      } else getQuestionBySelectedICDCodes(selectedChronic, () => { });
    }
  }, [isEdit, isView, selectedChronic]);

  const handleDownloadPdf = async () => {
    const id = careplanData?._id;
    if (!id) return;
    setIsDownloading(true);
    try {
      await downloadBackendPdf({
        url: `/treatment-plan/${id}/pdf`,
        fallbackFilename: "treatment-plan.pdf",
      });
    } catch (error) {
      getErrToast(error);
    } finally {
      setIsDownloading(false);
    }
  };
  if (IS_ROLE_CARETAKER) console.log({ caretakerId: user?.id }, patientData?.assignedCaretakerIds);
  console.log({ careplanData });

  // Load image and convert to base64

  return (
    <DashboardLayout activePage={isView ? "view-treatment-plan" : "Treatment Plan"} rightcomponent={<>
      {careplanData && isView && (
        <Button
          variant="outline"
          className="w-auto"
          onClick={handleDownloadPdf}
          disabled={isDownloading}
        >
          <Download className="w-4 h-4" />
          {!isMobile && (isDownloading ? "Preparing PDF..." : "PDF")}
        </Button>
      )}
    </>}>
      {isViewLoading && (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="mt-3 text-muted-foreground">Loading treatment plan...</p>
          </div>
        </div>
      )}
      {!isViewLoading && (
        <>
          {/* Device Type Selection Popup - Only popup shown */}
          {!isView && (
            <DeviceTypePopup
              isOpen={isDeviceTypePopupOpen}
              onCancel={handleDeviceTypePopupCancel}
              onDone={handleDeviceTypePopupDone}
              btnLoad={false}
              isEdit={isEdit}
              patientId={patientId}
              typeOfPatient={patientData?.typeOfPatient}
              seletedPreviousDeviceTypes={selectedDeviceTypes}
            />
          )}
          {/* {JSON.stringify(patientData?.programs)}  */}

          <div className="flex flex-col gap-4">
            <div className="shrink flex justify-end">



              {!isView && (
                <div className="space-y-1 w-full sm:w-60">
                  <Label>Select Caretaker</Label>
                  <Select
                    value={selectedCaretaker ?? ""}
                    onValueChange={setSelectedCaretaker}
                    disabled={
                      isView ||
                      (caretakerOptions || []).length == 0
                      ||
                      IS_ROLE_CARETAKER
                    }
                  >
                    <SelectTrigger className="">
                      <SelectValue placeholder={"Select caretaker"} />
                    </SelectTrigger>
                    <SelectContent className="">
                      {(caretakerOptions || []).map(
                        (opt: any) => (
                          <SelectItem value={opt?._id} className="">
                            {opt.firstName} {opt.lastName}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

            </div>

            {!isView ? (
              <div className="rounded-md border border-[#dddddd] bg-[#eeeeee] p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <span className="font-semibold text-[#6E6E6E] text-sm">PATIENT NAME:</span>
                    <span className="ml-2 text-sm text-black">
                      {[patientData?.firstName, patientData?.middleName, patientData?.lastName]
                        .filter(Boolean)
                        .join(" ")}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-[#6E6E6E] text-sm">DATE OF BIRTH:</span>
                    <span className="ml-2 text-sm text-black">
                      {patientData?.dateOfBirth ? formatDate(patientData.dateOfBirth) : "—"}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-[#6E6E6E] text-sm">Organization:</span>
                    <span className="ml-2 text-sm text-black">
                      {patientData?.organizationId?.organizationName || "—"}
                    </span>
                  </div>
                </div>

                {icdCodesTopDisplay && false ? (
                  <div className="mt-4 pt-3 border-t border-[#dddddd]">
                    <span className="font-semibold text-[#6E6E6E] text-sm">ICD CODES:</span>
                    <span className="ml-2 text-sm text-black">{icdCodesTopDisplay}</span>
                  </div>
                ) : null}
              </div>
            ) : null}
            {/* {chronicQuestions.length > 0 && ( */}

            {isView && <ViewTreatmentPlan treatmentPlan={careplanData} />}

            {!isView && <>
              {(isView
                ? isView
                : patientData && Object.keys(patientData).length > 0) &&
                (isEdit || isView ? true : selectedChronic.length > 0) && (
                  <div
                    className="flex-1 overflow-y-auto "
                    ref={careplanContentRef}
                    data-careplan-content
                  >
                    <RHFWrapper
                      validateSchema={{}}
                      initialValues={
                        careplanData
                          ? getCareplanValues(careplanData?.questions)
                          : {}
                      }
                      isValidate={false}
                      onSubmit={(data) => handleSubmitCareplan(data)}
                      className="space-y-4"
                    >
                      {allCategories.map((category: any) => {
                        return (
                          <div className="p-4 border rounded-xl space-y-4">
                            <h2 className="text-xl font-semibold">
                              {category.heading}
                            </h2>
                            <div className="space-y-4">
                              {category.questions.map((question: any) => {
                                // question.questionrandomid =question?.question; //
                                if (question.type == "single_input") {
                                  return (
                                    <RhfInput
                                      name={question?.questionrandomid}
                                      key={question?.questionrandomid}
                                      label={question?.question}
                                      type=""
                                      disabled={isView}
                                    />
                                  );
                                } else if (question.type == "textarea") {
                                  return (
                                    <RhfInput
                                      key={question?.questionrandomid}
                                      name={question?.questionrandomid}
                                      label={question?.question}
                                      type="textarea"
                                      disabled={isView}
                                    />
                                  );
                                } else if (question.type == "multi_input") {
                                  return (
                                    <RhfMultiInput
                                      key={question?.questionrandomid}
                                      name={question?.questionrandomid}
                                      label={question?.question}
                                      disabled={isView}
                                    />
                                  );
                                } else if (question.type == "single_select") {
                                  return (
                                    <>
                                      <RhfSelect
                                        options={question.options}
                                        name={question?.questionrandomid}
                                        label={question?.question}
                                        disabled={isView}
                                      />
                                    </>
                                  );
                                } else if (question.type == "multi_select") {
                                  return (
                                    <RhfSelect
                                      key={question?.questionrandomid}
                                      options={question.options}
                                      name={question?.questionrandomid}
                                      label={question?.question}
                                      multiple
                                      disabled={isView}
                                    />
                                  );
                                } else if (question.type == "single_choice") {
                                  return (
                                    <SingleChoice
                                      question={question}
                                      isView={isView}
                                    />
                                  );
                                  return (
                                    <div className="flex flex-col gap-1">
                                      <RhfChecked
                                        key={question?.questionrandomid}
                                        options={question.options}
                                        name={question?.questionrandomid}
                                        label={question?.question}
                                        disabled={isView}
                                      />
                                      <div className="flex items-center gap-2 mt-0">
                                        <h2 className="text-xs">Other: </h2>
                                        <RhfInput
                                          name={question?.questionrandomid}
                                          disabled={isView}
                                        />
                                      </div>
                                    </div>
                                  );
                                } else if (question.type == "multiple_choice") {
                                  return (
                                    <MultiChoice
                                      question={question}
                                      isView={isView}
                                    />
                                  );
                                  return (
                                    <RhfChecked
                                      options={question.options}
                                      name={question?.questionrandomid}
                                      label={question?.question}
                                      multiple
                                      disabled={isView}
                                    />
                                  );
                                }
                                else if (question.type == "date") {
                                  return (<RhfDateTimePicker
                                    key={question?.questionrandomid}
                                    name={question?.questionrandomid}
                                    label={question?.question}
                                    type="date"
                                    disabled={isView}
                                    isfutureDateDisabled
                                    placeholderDate="Select date"
                                  />)
                                }
                                else return null;
                              })}
                            </div>
                          </div>
                        );
                      })}
                      {!isView && (
                        <Button type="submit" disabled={btnLoad || !isCaretakerAssigned}>
                          Submit
                        </Button>
                      )}
                    </RHFWrapper>
                  </div>
                )}
            </>}
            {/* )} */}
          </div>
        </>)}
    </DashboardLayout>
  );
};
export default Careplan;



















const SingleChoice = ({ question, isView }: any) => {
  const value = useWatch({ name: question?.questionrandomid });
  const IS_INC =
    question.options &&
    Array.isArray(question.options) &&
    question.options.includes(value);

  return (
    <div className="flex flex-col gap-1">
      <RhfChecked
        key={question?.questionrandomid}
        options={question.options}
        name={question?.questionrandomid}
        label={question?.question}
        disabled={isView}
      />
      <div className="flex items-center gap-2 mt-0">
        <h2 className="text-xs">Other: </h2>
        {IS_INC ? (
          <RhfInput
            name={question?.questionrandomid}
            disabled={isView}
            value=""
          />
        ) : (
          <RhfInput name={question?.questionrandomid} disabled={isView} />
        )}
      </div>
    </div>
  );
};

const MultiChoice = ({ question, isView }: any) => {
  const value = useWatch({ name: question?.questionrandomid });
  const IS_INC =
    question.options &&
    Array.isArray(question.options) &&
    question.options.includes(value);
  return (
    <div className="flex flex-col gap-1">
      <RhfChecked
        key={question?.questionrandomid}
        options={question.options}
        name={question?.questionrandomid}
        label={question?.question}
        multiple
        disabled={isView}
      />
      <div className="flex items-center gap-2 mt-0">
        <h2 className="text-xs">Other: </h2>
        {Array.isArray(value) ? (
          <RhfInput
            name={question?.questionrandomid}
            disabled={isView}
            value=""
          />
        ) : (
          <RhfInput name={question?.questionrandomid} disabled={isView} />
        )}
      </div>
    </div>
  );
};


